import { WithTranslation, withTranslation } from 'react-i18next';
import PartnerTableBar from 'modules/partner/components/PartnerTableBar';
import { PartnerCompanyModel, PartnerCompanySelectModel } from 'logic/partner/logic/partner-partners';
import PartnersTable from 'modules/partner/modules/partners/components/PartnerTable';
import PartnerPairing from 'modules/partner/components/PartnerPairing';
import { PairingItemType, CurrencySelectModel, CountrySelectModel } from 'logic/partner/partner';
import { PaginationParams, PaginatedResponse } from 'common/model/pagination';
import { LayoutContent } from 'common/components/Layout/Content';
import { PartnerPairingKey, PartnerPairingModel } from 'common/model/partner-pairing';
import TableBar from 'common/components/TableBar';
import Collapse from 'common/components/Collapse';
import { LayoutSidePanel } from 'common/components/Layout/SidePanel';
import PartnersDetailCard from 'modules/partner/modules/partners/components/PartnersDetailCard';
import PartnersLinkedItems from 'modules/partner/modules/partners/components/PartnersLinkedItems';
import PartnersActions from 'modules/partner/modules/partners/components/PartnersActions';

interface Props extends WithTranslation {
    edit: boolean;
    loading: boolean;
    data?: PaginatedResponse<PartnerCompanyModel[]>;
    selected?: PartnerCompanyModel;
    search?: { text: string };
    pairing?: PartnerPairingModel;
    companies?: PartnerCompanySelectModel[];
    selectedCompany?: PartnerCompanySelectModel;
    currencyCounter?: CurrencySelectModel[];
    countryCounter?: CountrySelectModel[];
    createLoading?: boolean;
    createError?: string;
    onBarCompanyFilterChange?: (id: string) => void;
    onBarCompanyFilterCancel?: () => void;
    onBarCompanyFilterSearch?: (value: string) => void;
    onBarActionsCreate?: () => void;
    onTableRowSelect?: (id: string) => void;
    onDetailActionsUnpair?: (linked: PairingItemType) => void;
    onCheckUserEmailExist?: (email: string) => Promise<boolean>;
    onPairingKeySelect?: (key: PartnerPairingKey) => void;
    onPairingItemSelect?: (itemId: string) => void;
    onPairingSearch?: (text: string) => void;
    onPairingConfirm?: () => void;
    onPairingCancel?: () => void;
    onActionsImpersonate?: () => Promise<void>;
    onActionsPair?: () => void;
    onPaginationChange?: (pagination: PaginationParams) => void;
}

function Partners(props: Props) {
    const { t } = props;

    return (
        <>
            {props.pairing && (
                <PartnerPairing
                    keys={props.pairing.keys}
                    keySelected={props.pairing.keySelected}
                    items={props.pairing.items}
                    itemIdSelected={props.pairing.itemIdSelected}
                    search={props.pairing.search}
                    loading={props.pairing.loading}
                    keySource={PartnerPairingKey.companies}
                    onKeySelect={props.onPairingKeySelect}
                    onItemSelect={props.onPairingItemSelect}
                    onSearch={props.onPairingSearch}
                    onConfirm={props.onPairingConfirm}
                    onCancel={props.onPairingCancel}
                />
            )}

            <LayoutContent
                className="partner-companies no-padding"
                mainSizes={{ xs: 24, sm: 24, md: 18 }}
                extraSizes={[{ xs: 24, sm: 24, md: 6 }]}
                main={
                    <>
                        <TableBar
                            heading={t('common.companies')}
                            filters={[
                                <PartnerTableBar
                                    companies={props.companies}
                                    selectedCompany={props.selectedCompany}
                                    onCompanyFilterChange={props.onBarCompanyFilterChange}
                                    onCompanyFilterCancel={props.onBarCompanyFilterCancel}
                                    onCompanyFilterSearch={props.onBarCompanyFilterSearch}
                                />
                            ]}
                        />
                        <PartnersTable
                            data={props.data}
                            selected={props.selected}
                            loading={props.loading}
                            onTableRowSelect={props.onTableRowSelect}
                            onPaginationChange={props.onPaginationChange}
                        />
                    </>
                }
                extra={[
                    props.selected ? (
                        <LayoutSidePanel
                            header={props.selected.name}
                            body={
                                <Collapse expandIconPosition="right" bordered={true} defaultActiveKey={['1', '2']}>
                                    <Collapse.Panel header={props.t('common.detail')} key="1">
                                        <PartnersDetailCard
                                            data={props.selected}
                                            countryCounter={props.countryCounter}
                                            currencyCounter={props.currencyCounter}
                                        />
                                    </Collapse.Panel>
                                    {!props.edit ? (
                                        <Collapse.Panel header={props.t('Partner.linkedItems')} key="2">
                                            <PartnersLinkedItems
                                                model={props.selected}
                                                onUnpair={props.onDetailActionsUnpair}
                                                unpairHide
                                            />
                                        </Collapse.Panel>
                                    ) : (
                                        <></>
                                    )}
                                </Collapse>
                            }
                            footer={
                                !props.edit && (
                                    <PartnersActions
                                        onImpersonate={
                                            props.selected?.contactPerson?.[0]?.ssoId
                                                ? props.onActionsImpersonate
                                                : undefined
                                        }
                                    />
                                )
                            }
                        />
                    ) : null
                ]}
            />
        </>
    );
}

export default withTranslation()(Partners);
